import { Check, Loader2, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { JobPreferences } from '../hooks/useJobPreferences';
import { useJobPreferences } from '../hooks/useJobPreferences';

const MAX_SKILLS = 20;

const JOB_TYPE_VALUES = ['', 'Full-time', 'Part-time', 'Contract', 'Internship'] as const;
const EXPERIENCE_VALUES = ['', 'Junior', 'Mid', 'Senior', 'Lead'] as const;

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'] as const;

const inputClass =
  'w-full min-h-[44px] rounded-lg border vscode-border vscode-bg-secondary vscode-text-primary text-sm px-3 py-2 ' +
  'focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)]';
const labelClass = 'block text-xs font-semibold vscode-text-secondary mb-1.5';
const selectClass =
  'w-full min-h-[44px] rounded-lg border vscode-border vscode-bg-secondary vscode-text-primary text-sm px-3 py-2 ' +
  'focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] appearance-none';

function parseOptionalInt(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : null;
}

const JobPreferencesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { prefs, loading, saving, save, error, isSignedIn } = useJobPreferences();

  const [skills, setSkills] = useState<string[]>([]);
  const [skillDraft, setSkillDraft] = useState('');
  const [domain, setDomain] = useState('');
  const [jobType, setJobType] = useState('');
  const [experience, setExperience] = useState('');
  const [location, setLocation] = useState('');
  const [remotePreferred, setRemotePreferred] = useState(false);
  const [salaryMinStr, setSalaryMinStr] = useState('');
  const [salaryMaxStr, setSalaryMaxStr] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState<string>('INR');

  const [savedToast, setSavedToast] = useState(false);
  const lastSaveClick = useRef(0);

  const jobTypeLabel = (value: string) => {
    if (!value) return t('dashboard.jobs.jobPreferences.any', 'Any');
    if (value === 'Full-time') return t('dashboard.jobs.jobPreferences.jobType.fulltime', 'Full-time');
    if (value === 'Part-time') return t('dashboard.jobs.jobPreferences.jobType.parttime', 'Part-time');
    if (value === 'Contract') return t('dashboard.jobs.jobPreferences.jobType.contract', 'Contract');
    if (value === 'Internship') return t('dashboard.jobs.jobPreferences.jobType.internship', 'Internship');
    return value;
  };

  const experienceLabel = (value: string) => {
    if (!value) return t('dashboard.jobs.jobPreferences.any', 'Any');
    if (value === 'Junior') return t('dashboard.jobs.jobPreferences.experience.junior', 'Junior');
    if (value === 'Mid') return t('dashboard.jobs.jobPreferences.experience.mid', 'Mid');
    if (value === 'Senior') return t('dashboard.jobs.jobPreferences.experience.senior', 'Senior');
    if (value === 'Lead') return t('dashboard.jobs.jobPreferences.experience.lead', 'Lead');
    return value;
  };

  useEffect(() => {
    if (!prefs) return;
    setSkills(prefs.skills?.length ? [...prefs.skills] : []);
    setDomain(prefs.domain ?? '');
    setJobType(prefs.job_type ?? '');
    setExperience(prefs.experience_level ?? '');
    setLocation(prefs.location ?? '');
    setRemotePreferred(prefs.remote_preferred);
    setSalaryMinStr(prefs.salary_min != null ? String(prefs.salary_min) : '');
    setSalaryMaxStr(prefs.salary_max != null ? String(prefs.salary_max) : '');
    setSalaryCurrency(prefs.salary_currency ?? 'INR');
  }, [prefs]);

  const addSkillsFromText = useCallback((raw: string) => {
    const parts = raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setSkills(prev => {
      const next = [...prev];
      for (const p of parts) {
        if (next.length >= MAX_SKILLS) break;
        if (!next.some(s => s.toLowerCase() === p.toLowerCase())) {
          next.push(p);
        }
      }
      return next;
    });
  }, []);

  const onSkillKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const draft = skillDraft.trim();
      if (!draft) return;
      addSkillsFromText(draft);
      setSkillDraft('');
      return;
    }
    if (e.key === ',') {
      e.preventDefault();
      const draft = skillDraft.trim();
      if (draft) addSkillsFromText(draft);
      setSkillDraft('');
    }
  };

  const removeSkill = (s: string) => {
    setSkills(prev => prev.filter(x => x !== s));
  };

  const handleSave = async () => {
    const now = Date.now();
    if (now - lastSaveClick.current < 500) return;
    lastSaveClick.current = now;
    setSavedToast(false);

    const payload: Partial<JobPreferences> = {
      skills,
      domain: domain.trim() || null,
      job_type: jobType || null,
      experience_level: experience || null,
      location: location.trim() || null,
      remote_preferred: remotePreferred,
      salary_min: parseOptionalInt(salaryMinStr),
      salary_max: parseOptionalInt(salaryMaxStr),
      salary_currency: salaryCurrency || 'INR',
    };

    try {
      await save(payload);
      setSavedToast(true);
    } catch {
      /* error surfaced via hook */
    }
  };

  useEffect(() => {
    if (!savedToast) return;
    const timer = window.setTimeout(() => setSavedToast(false), 1500);
    return () => window.clearTimeout(timer);
  }, [savedToast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 rounded-xl border vscode-border vscode-bg-secondary">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--vscode-primary)]" aria-hidden />
        <span className="sr-only">{t('dashboard.jobs.jobPreferences.loading', 'Loading job preferences')}</span>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="rounded-xl border vscode-border vscode-bg-secondary p-4 sm:p-5">
        <p className="text-sm vscode-text-secondary text-center">
          {t('dashboard.jobs.jobPreferences.signInHint', 'Sign in to personalise your feed')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border vscode-border vscode-bg-secondary p-4 sm:p-5 flex flex-col gap-4 min-w-0">
      {error && (
        <div
          className="text-sm rounded-lg border px-3 py-2"
          style={{
            borderColor: 'var(--vscode-error-border)',
            background: 'var(--vscode-error-bg)',
            color: 'var(--vscode-error)',
          }}
        >
          {error}
        </div>
      )}

      {savedToast && !error && (
        <div
          className="flex items-center gap-2 text-sm rounded-lg border px-3 py-2"
          style={{
            borderColor: 'var(--vscode-success-border)',
            background: 'var(--vscode-success-bg)',
            color: 'var(--vscode-success)',
          }}
        >
          <Check className="w-4 h-4 shrink-0" />
          {t('dashboard.jobs.jobPreferences.saved', 'Saved')}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:max-w-none">
        <div>
          <label className={labelClass} htmlFor="jp-skills">
            {t('dashboard.jobs.jobPreferences.skills', 'Skills')}
          </label>
          <input
            id="jp-skills"
            type="text"
            value={skillDraft}
            onChange={e => setSkillDraft(e.target.value)}
            onKeyDown={onSkillKeyDown}
            placeholder={t('dashboard.jobs.jobPreferences.skillsPlaceholder', 'Type a skill, press Enter or comma')}
            disabled={saving}
            className={inputClass}
            autoComplete="off"
          />
          {skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {skills.map(s => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 max-w-full pl-2 pr-1 py-1 rounded-md text-[11px] font-medium bg-[var(--vscode-primary)]/10 vscode-text-primary border border-[var(--vscode-primary)]/20"
                >
                  <span className="truncate">{s}</span>
                  <button
                    type="button"
                    onClick={() => removeSkill(s)}
                    disabled={saving}
                    className="shrink-0 p-1 rounded hover:bg-[var(--vscode-primary)]/15 touch-manipulation min-w-[28px] min-h-[28px] flex items-center justify-center"
                    aria-label={t('dashboard.jobs.jobPreferences.removeSkill', { defaultValue: 'Remove {{skill}}', skill: s })}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] vscode-text-tertiary mt-1">
            {t('dashboard.jobs.jobPreferences.skillsLimit', {
              defaultValue: 'Up to {{max}} skills ({{count}}/{{max}})',
              max: MAX_SKILLS,
              count: skills.length,
            })}
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="jp-domain">
            {t('dashboard.jobs.jobPreferences.domain', 'Domain')}
          </label>
          <input
            id="jp-domain"
            type="text"
            maxLength={60}
            value={domain}
            onChange={e => setDomain(e.target.value)}
            disabled={saving}
            placeholder={t('dashboard.jobs.jobPreferences.domainPlaceholder', 'e.g. FinTech, SaaS')}
            className={inputClass}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="flex-1 min-w-0">
            <label className={labelClass} htmlFor="jp-job-type">
              {t('dashboard.jobs.jobPreferences.jobTypeLabel', 'Job type')}
            </label>
            <select
              id="jp-job-type"
              value={jobType}
              onChange={e => setJobType(e.target.value)}
              disabled={saving}
              className={selectClass}
            >
              {JOB_TYPE_VALUES.map(value => (
                <option key={value || 'any'} value={value}>
                  {jobTypeLabel(value)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-0">
            <label className={labelClass} htmlFor="jp-exp">
              {t('dashboard.jobs.jobPreferences.experienceLabel', 'Experience level')}
            </label>
            <select
              id="jp-exp"
              value={experience}
              onChange={e => setExperience(e.target.value)}
              disabled={saving}
              className={selectClass}
            >
              {EXPERIENCE_VALUES.map(value => (
                <option key={value || 'any'} value={value}>
                  {experienceLabel(value)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass} htmlFor="jp-loc">
            {t('dashboard.jobs.jobPreferences.location', 'Location')}
          </label>
          <input
            id="jp-loc"
            type="text"
            value={location}
            onChange={e => setLocation(e.target.value)}
            disabled={saving}
            placeholder={t('dashboard.jobs.jobPreferences.locationPlaceholder', 'City, region, or country')}
            className={inputClass}
          />
        </div>

        <label className="flex items-center gap-3 min-h-[44px] cursor-pointer touch-manipulation">
          <input
            type="checkbox"
            checked={remotePreferred}
            onChange={e => setRemotePreferred(e.target.checked)}
            disabled={saving}
            className="w-4 h-4 rounded border vscode-border accent-[var(--vscode-primary)]"
          />
          <span className="text-sm vscode-text-primary">{t('dashboard.jobs.jobPreferences.remotePreferred', 'Remote preferred')}</span>
        </label>

        <div>
          <span className={labelClass}>{t('dashboard.jobs.jobPreferences.salaryRange', 'Salary range (optional)')}</span>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 min-w-0">
              <label className="text-[10px] vscode-text-tertiary mb-1 block" htmlFor="jp-smin">
                {t('dashboard.jobs.jobPreferences.salaryMin', 'Min')}
              </label>
              <input
                id="jp-smin"
                type="text"
                inputMode="numeric"
                value={salaryMinStr}
                onChange={e => setSalaryMinStr(e.target.value.replace(/[^\d-]/g, ''))}
                disabled={saving}
                placeholder="—"
                className={inputClass}
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-[10px] vscode-text-tertiary mb-1 block" htmlFor="jp-smax">
                {t('dashboard.jobs.jobPreferences.salaryMax', 'Max')}
              </label>
              <input
                id="jp-smax"
                type="text"
                inputMode="numeric"
                value={salaryMaxStr}
                onChange={e => setSalaryMaxStr(e.target.value.replace(/[^\d-]/g, ''))}
                disabled={saving}
                placeholder="—"
                className={inputClass}
              />
            </div>
            <div className="w-full sm:w-32 shrink-0">
              <label className="text-[10px] vscode-text-tertiary mb-1 block" htmlFor="jp-cur">
                {t('dashboard.jobs.jobPreferences.currency', 'Currency')}
              </label>
              <select
                id="jp-cur"
                value={salaryCurrency}
                onChange={e => setSalaryCurrency(e.target.value)}
                disabled={saving}
                className={selectClass}
              >
                {CURRENCIES.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving}
        className="min-h-[44px] w-full sm:w-auto px-5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60 touch-manipulation flex items-center justify-center gap-2"
        style={{ background: 'var(--brand-cta-bg, var(--vscode-primary))' }}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {saving ? t('dashboard.jobs.jobPreferences.saving', 'Saving…') : t('dashboard.jobs.jobPreferences.save', 'Save')}
      </button>
    </div>
  );
};

export default React.memo(JobPreferencesPanel);
